import { defineStore } from 'pinia'
import {APIRoute, EP} from "@/src/endpoints";
import { getDataForEntity } from "@/src/utils";
import beApiClient from "@/src/be-api-client";
import { AxiosResponse } from 'axios';
import {InternalQARow} from '@/src/models/dashboard/internal-qa-row';
import { handleErrors, makeRequest } from "@/src/utils";
import { QADashboard } from "@/src/models/dashboard/qa-dashboard";
import {SaveStates} from "@/src/enums";

type Lookup = { 
  label: string;
  value: string;
}

export const useDashboardsInternalQAStore = defineStore('dashboards-internal-qa', {
  state: (): { 
    transplantCenters: null|Array<Lookup>,
    organs: null|Array<Lookup>,
    organGroups: null|Array<Lookup>,
    nonRecoveries: null|Array<Lookup>,
    finalDispositionCodes: null|Array<Lookup>,
    matchServiceLevels: null|Array<Lookup>,
    matchServiceLevelErrors: null|Array<Lookup>,
    checklistStatuses: null|Array<{code: string, label: string}>,
    filterParams: any,
    dashboardContent: {
      records: null|Array<InternalQARow>,
      totalRows: number|null
    },
    visibleContent: Array<InternalQARow>,
    failedRows: Array<InternalQARow>
  } => {
    return {
      transplantCenters: null,
      organs: null,
      organGroups: null,
      nonRecoveries: null,
      finalDispositionCodes: null,
      matchServiceLevels: null,
      matchServiceLevelErrors: null,
      checklistStatuses: null,
      filterParams: {},
      dashboardContent: {
        totalRows: null,
        records: []
      },
      visibleContent: [],
      failedRows: []
    }
  },
  getters: {
    isDirty: (state) => (index) => {
      if(!index) return false; 
      return state.visibleContent[index].dirty;      // return JSON.stringify(original) !== JSON.stringify(edited)
    }
  },
  actions: {
    loadLookups() {
      return Promise.all([
        this.loadTransplantCenters(),
        this.loadOrganGroups(),
        this.loadMatchServiceLevels(),
        this.loadMatchServiceLevelErrors(),
        this.loadNonRecoveries(),
        this.loadFinalDispositions(),
        this.loadChecklistStatuses()
      ])
    },
    loadTransplantCenters() {
      return getDataForEntity(this, 'transplantCenters', APIRoute(EP.dashboards.internal_qa.lookups.transplant_centers))
    },
    loadOrganGroups() {
      return getDataForEntity(this, 'organGroups', APIRoute(EP.dashboards.internal_qa.lookups.organ_groups))
    },
    loadMatchServiceLevels() {
      return getDataForEntity(this, 'matchServiceLevels', APIRoute(EP.dashboards.internal_qa.lookups.match_service_levels))
    },
    loadMatchServiceLevelErrors() {
      return getDataForEntity(this, 'matchServiceLevelErrors', APIRoute(EP.dashboards.internal_qa.lookups.match_service_level_errors))
    },
    loadNonRecoveries() {
      return getDataForEntity(this, 'nonRecoveries', APIRoute(EP.dashboards.internal_qa.lookups.non_recoveries))
    },
    loadFinalDispositions() {
      return getDataForEntity(this, 'finalDispositionCodes', APIRoute(EP.dashboards.internal_qa.lookups.final_dispositions))
    },
    loadChecklistStatuses() {
      return getDataForEntity(this, 'checklistStatuses', APIRoute(EP.dashboards.internal_qa.lookups.checklist_statuses))
    },
    // TODO: Make the required params required
    loadDashboardData(params: {
      transplant_center_id?: string;
      organ_group_id?: string;
      for_month?: any;
    } = {}) {
      const url = APIRoute(EP.dashboards.internal_qa.show);
      return new Promise<void>((resolve, reject) => {
        beApiClient.get(url, {
          headers: {'Accept': 'application/json'},
          params: {
            filter: {
              transplant_center_id: params.transplant_center_id,
              organ_group_id: params.organ_group_id,
              for_month: {
                month: params.for_month.month + 1,
                year: params.for_month.year
              }
            }
          } 
        }).then((response: AxiosResponse) => {
          this.dashboardContent.totalRows = response.data.count;
          this.dashboardContent.rows = response.data.records.map((r: any) => {
            return InternalQARow.buildFromAPI(r);
          });
          // This will ensure all nested objects are independent, so changing visibleContent won't affect dashboardContent
          this.visibleContent = this.dashboardContent.rows.map(p => JSON.parse(JSON.stringify(p)));
          resolve();
        }).catch((error) => {
          reject(error);
        });
      }); 
    },
    applySort(filterParams: any) {
      this.visibleContent = this.dashboardContent.rows.filter((row: InternalQARow) => {
        
        if (filterParams.match_applicable_service_level?.length  && 
          !filterParams.match_applicable_service_level.includes(row.calculated_stats.applicable_service_level)) { 
          return null;
        }

        if (filterParams.match_service_level_error?.length  && 
          !filterParams.match_service_level_error.includes(row.calculated_stats.service_level_error)) { 
          return null;
        }

        if (filterParams.match_final_disposition_applicable?.length && 
          !filterParams.match_final_disposition_applicable.includes(row.calculated_stats.final_disposition_applicable?.code)) { 
          return null;
        }

        if (filterParams.match_decline_physician?.length) {
          if (row.physician
                ? !filterParams.match_decline_physician.includes(row.physician)
                : !filterParams.match_decline_physician.includes('BLANK')) {
            return null;
          }
        }
        
        if (filterParams.match_auto_rule_out?.length && 
          !filterParams.match_auto_rule_out.includes(row.auto_rule_out ?? false)) { 
          return null;
        }


        if(filterParams.match_code && !row.code.toString().includes(filterParams.match_code)) {
          return null;
        }

        if(filterParams.donor_code && row.donor && 
           !row.donor.code.toString().toLowerCase().includes(filterParams.donor_code.toLowerCase())) {
          return null;
        }
        
        // Checklist status filter has two matching tracks because 'not_started'
        // isn't a value stored on any checklist record.
        // A row is "not started" when it has no checklists at all. Other statuses ('in_progress','verified')
        // match if *any* checklist on the row carries that status.
        // The empty-string filter value means "All" and skips filtering entirely.
        if (filterParams.checklist_status?.length && !filterParams.checklist_status.includes('')) {
          const statuses = filterParams.checklist_status.filter(s => s != null);
          if (statuses.length) {
            const rowStatuses = (row.checklists || []).map(c => c.status);
            const includeNotStarted = statuses.includes('not_started');
            const otherStatuses = statuses.filter(s => s !== 'not_started');
            const matchesNotStarted = includeNotStarted && rowStatuses.length === 0;
            const matchesOther = otherStatuses.some(s => rowStatuses.includes(s));
            if (!matchesNotStarted && !matchesOther) {
              return null;
            }
          }
        }

        if (filterParams.offer_approval_type?.length) {
          const matches =
            (!filterParams.offer_approval_type.includes("fd_approved") || row.final_disposition_approved) &&
            (!filterParams.offer_approval_type.includes("fd_unapproved") || !row.final_disposition_approved) &&
            (!filterParams.offer_approval_type.includes("sl_approved") || row.service_level_approved) &&
            (!filterParams.offer_approval_type.includes("sl_unapproved") || !row.service_level_approved);

            if (!matches) return null;
        }

        return row;
      }).map((row: InternalQARow) => { 
        return JSON.parse(JSON.stringify(row)); // Ensure we are working with a deep copy
      })
    },
    onSaveRow(data, filterParams) {
      //matches with same donor
      let relatedIds = this.getRelatedRowIds(data.donor.id);
      relatedIds.forEach(id => this.visibleContent.map(c => {
        if(c.id == id) { 
          c.current_state = SaveStates.Processing;
        }
      }));
      const qaDashboard = new QADashboard(data);
      const payload = qaDashboard.payloadForApi();

      return new Promise<void>((resolve, reject) => {
        beApiClient.post(APIRoute(EP.dashboards.internal_qa.update, { id: data.id } ), payload).then(response => {     
          // Refresh all related rows
          Promise.all(
            relatedIds.map(id => this.onRefreshRow(id, filterParams))
          ).then(() => {
            setTimeout(() => {
                // Only update to success green if the row still exists (it may have been filtered out after changes)
                const rowIndex = this.visibleContent.findIndex(row => row.id === data.id);
                if (rowIndex !== -1) {
                  this.visibleContent[rowIndex].current_state = SaveStates.Success;
                }
            }, 2000)
           
            setTimeout(() => {
              relatedIds.forEach(id => this.visibleContent.map(c => {
                if(c.id == id)  {
                  c.dirty = false;
                  c.current_state = SaveStates.Idle;
                }
              }));
            }, 3000)

            // remove the row from failedRows if it exists
            this.removeFromFailedRowsById(data.id);

            resolve();
          });
        }).catch(error => {
          const rowIndex = this.visibleContent.findIndex(row => row.id === data.id);

          // Get the error message
          const message = handleErrors(error.response.data.error);
          if (rowIndex !== -1) {
            this.visibleContent[rowIndex].current_state = SaveStates.Errored;
            this.visibleContent[rowIndex].errors = message;
            
            // Add the row to failedRows if it doesn't already exist
            const exists = this.failedRows?.some(r => r.id === data.id );
            this.failedRows = exists ? this.failedRows : [...this.failedRows, this.visibleContent[rowIndex]];
          }

          // Reset the state of all related rows to Idle or Blank based on if being edited.
          this.visibleContent.forEach((c, idx) => {
            if (relatedIds.includes(c.id) && c.id !== data.id) { // skip the row that triggered the error
              if (!c.dirty) 
                c.current_state = SaveStates.Idle;
              else 
                c.current_state = SaveStates.Blank; 
            }
          });

          reject(message);
        }).finally(() => {
          
        })
      });

    },
    removeFromFailedRowsById(id: string) {
      if (!this.failedRows) return;
      this.failedRows = this.failedRows.filter(r => r.id !== id);
    },
    getRelatedRowIds(donorId) {
      return this.dashboardContent.rows.filter(r => r.donor.id === donorId).map(r => r.id);
    },
    markDirty(id: string) {
      const targetIndex = this.visibleContent.findIndex(row => row.id === id);
      if (targetIndex === -1) return;
      
      this.visibleContent[targetIndex].dirty = true;
      this.visibleContent[targetIndex].current_state = SaveStates.Blank;
      this.visibleContent[targetIndex] = { ...this.visibleContent[targetIndex] };
    },
    resetRow(id: string) {
      const targetIndex = this.visibleContent.findIndex(row => row.id === id);
      if (targetIndex !== -1) {
        const originalRow = this.dashboardContent.rows.find(r => r.id === id);
        if (originalRow) {
          this.visibleContent[targetIndex] = JSON.parse(JSON.stringify(originalRow));
          this.removeFromFailedRowsById(id);
        }
      }
    },
    onRefreshRow(matchId, filterParams) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.get(APIRoute(EP.dashboards.internal_qa.retrieve, { id: matchId })).then((response: AxiosResponse) => {
          this.dashboardContent.rows.find((r, index) => {
            if(r.id === response.data.id) this.dashboardContent.rows[index] =  InternalQARow.buildFromAPI(response.data);
          }) 

          const updatedRow = this.dashboardContent.rows.find(r => r.id === matchId);
          const idx = this.visibleContent.findIndex(r => r.id === matchId);
          if (idx !== -1) {
            this.visibleContent[idx] = JSON.parse(JSON.stringify(updatedRow));
          }

          this.removeFromFailedRowsById(updatedRow.id);

          resolve();
        }).catch(error => {
          console.log(error);
          const message = handleErrors(error);
          reject(message);
        })
      })
    },
    saveView(payload, preferenceType) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.patch(APIRoute(EP.dashboards.internal_qa.saveView, { preference_type: encodeURIComponent(preferenceType) } ), payload).then(response => {     
         
          resolve(response);
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
    saveFinalDispositionApprovals(payload) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.post(APIRoute(EP.dashboards.internal_qa.saveFinalDispositionApprovals ), {match: payload}).then(response => {  
          payload.map(match => {
            this.dashboardContent.rows.find((r, index) => {
              if(r.id === match.id) r['final_disposition_approved'] = match.approved;
            });

            this.visibleContent.find((r, index) => {
              if(r.id === match.id) r['final_disposition_approved'] = match.approved;
            });
          });     
          resolve();
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
    saveServiceLevelApprovals(payload) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.post(APIRoute(EP.dashboards.internal_qa.saveServiceLevelApprovals ), {match: payload}).then(response => {  
          payload.map(match => {
            this.dashboardContent.rows.find((r, index) => {
              if(r.id === match.id) r['service_level_approved'] = match.approved;
            });

            this.visibleContent.find((r, index) => {
              if(r.id === match.id) r['service_level_approved'] = match.approved;
            });
          });   
          resolve();
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
    loadViews() {
      return new Promise<void>((resolve, reject) => {
        beApiClient.get(APIRoute(EP.dashboards.internal_qa.loadViews )).then(response => {

          resolve(response.data.preferences);
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
    deleteView(preferenceType) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.delete(APIRoute(EP.dashboards.internal_qa.deleteView, { preference_type: encodeURIComponent(preferenceType) })).then(response => {
          resolve(response);
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
    renameView(preferenceType, newName) {
      return new Promise<void>((resolve, reject) => {
        beApiClient.patch(APIRoute(EP.dashboards.internal_qa.renameView, { preference_type: encodeURIComponent(preferenceType) }), { new_name: newName }).then(response => {
          resolve(response);
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        })
      });
    },
  }
});